import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { titleize, humanize, decapitalize } from 'underscore.string';
import Switch from 'react-switch';
import SessionStore from 'stores/SessionStore';
import CargomaticCard from 'components/v2/card';
import CargomaticModal from 'components/v2/modal';
import CargomaticSelect from 'components/v2/select';
import CargomaticInput from 'components/v2/input';
import UserWebAPIUtils from 'utils/UserWebAPIUtils';
import { getCreditCards } from 'utils/CustomersAPIUtils';
import LabelValue from 'components/ui/tailwind/label-value';
import { stateCodes } from 'components/modules/user/user-form-utils';
import ReferenceNumberTypes from 'constants/ReferenceNumberTypes';
import { getAddressString, hasAddress, } from 'utils/transformers/AddressTransformer';
import { withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import { unescapeSpecialChars } from 'src/main/js/helpers/helper_functions';
import Multiselect from 'components/v2/multi-select';
import { LINES_OF_BUSINESS } from 'config/lines-of-business';
import PhoneField from 'components/v2/forms/PhoneField';
import { createPhoneNumberWithExtension } from 'utils/formatters/Phone';
import UserStore from '../../../../stores/UserStore';
import ToastStore from '../../../../stores/ToastStore';
import UserActionCreators from '../../../../actions/UserActionCreators';
import EditButton from '../../../v2/buttons/EditButton';
import Badge from '../../../v2/Badge';
import Capability from '../Capability';
import CarrierPreferences from './carrier-preferences';
import { Avatar } from '../Avatar';
import { SHIPPER_SUBSIDIARY_OPTIONS } from 'constants/subsidiary';
const MAX_MARKUP = 0.7;
const REQUIRED_ATTRIBUTES = [
    'first_name',
    'last_name',
    'username',
    'email',
    'street',
    'city',
    'state',
    'zipcode',
];
const MAX_TERM = 120;
const MODAL_TYPES = {
    EDIT: 'EDIT',
    SETTINGS: 'SETTINGS',
};
const getRequiredAsterisk = (field) => {
    return REQUIRED_ATTRIBUTES.includes(field) ? '*' : '';
};
const loginAs = async (grantorId) => {
    const admin = SessionStore.getUser();
    try {
        const user = await UserWebAPIUtils.loginAs(admin._id, grantorId).catch((err) => {
            ToastStore.getNotifications().push({
                title: 'Error',
                body: err,
                level: 'error',
            });
        });
        if (user) {
            window.location.href = '/';
        }
    }
    catch (err) {
        ToastStore.getNotifications().push({
            title: 'Error',
            body: err,
            level: 'error',
        });
    }
};
class ShipperDetails extends Component {
    constructor(props) {
        super(props);
        this._onUserStoreChange = () => {
            const _salesReps = UserStore.getSalesReps() || [];
            const _activeAdmins = UserStore.getAdminUsers() || [];
            const salesReps = _salesReps.map((salesPerson) => ({
                slug: salesPerson.email,
                name: salesPerson.email,
            }));
            const activeAdminReps = _activeAdmins.map((activeAdmin) => ({
                slug: activeAdmin.email,
                name: activeAdmin.email,
            }));
            this.setState({
                salesReps,
                activeAdminReps: _.sortBy(activeAdminReps, 'name'),
            });
        };
        this.validateFields = (parent, fields) => {
            const { t } = this.props;
            this.setState((prevState) => {
                const errors = Object.assign({}, prevState.errors);
                fields.forEach((field) => {
                    errors[field] =
                        !parent[field] && REQUIRED_ATTRIBUTES.includes(field)
                            ? {
                                message: t('FIELD_REQUIRED_TIP', {
                                    field: titleize(t(sluggify(field))),
                                }),
                            }
                            : {};
                });
                return { errors };
            });
        };
        this.handleAddressFieldChange = (field, value) => {
            const { t } = this.props;
            this.setState((prevState) => {
                const shipper = Object.assign({}, prevState.shipper);
                const changed = Object.assign({}, prevState.changed);
                const errors = Object.assign({}, prevState.errors);
                const address = shipper.address;
                address[field] = value;
                shipper.address = address;
                changed.address = true;
                errors[field] =
                    !value && REQUIRED_ATTRIBUTES.includes(field)
                        ? {
                            message: t('FIELD_REQUIRED_TIP', {
                                field: titleize(t(sluggify(field))),
                            }),
                        }
                        : {};
                return {
                    shipper,
                    changed,
                    errors,
                };
            });
        };
        this.handleFieldChange = (field, value) => {
            const { shipper, changed, errors } = this.state;
            const { t } = this.props;
            shipper[field] = value;
            changed[field] = true;
            errors[field] =
                !value && REQUIRED_ATTRIBUTES.includes(field)
                    ? {
                        message: t('FIELD_REQUIRED_TIP', {
                            field: titleize(t(sluggify(field))),
                        }),
                    }
                    : {};
            if (field === 'markup' && value && parseFloat(value) > MAX_MARKUP) {
                errors[field] = { message: t('MARK_UP_EXCEEED_MAX_TIP') };
            }
            if (field === 'drayage_terms' || field === 'shorthaul_terms') {
                if (Number(value) > MAX_TERM || Number(value) < 0) {
                    errors[field] = {
                        message: t('SHOULD_BE_BETWEEN_NUM1_NUM2', { num1: 0, num2: 20 }),
                    };
                }
                if (Number(value) > MAX_TERM || Number(value) < 1) {
                    errors[field] = {
                        message: t('SHOULD_BE_BETWEEN_NUM1_NUM2', { num1: 1, num2: 120 }),
                    };
                }
            }
            this.setState({ shipper, changed, errors });
        };
        this.handleArrayFieldChange = (field, value, enumValue) => {
            const { shipper, changed, errors } = this.state;
            const { t } = this.props;
            const fieldArray = shipper[field];
            shipper[field] = value
                ? fieldArray.concat([enumValue])
                : fieldArray.filter((column) => column !== enumValue);
            changed[field] = true;
            errors[field] =
                !value && REQUIRED_ATTRIBUTES.includes(field)
                    ? {
                        message: t('FIELD_REQUIRED_TIP', {
                            field: titleize(t(sluggify(field))),
                        }),
                    }
                    : {};
            this.setState({ shipper, changed, errors });
        };
        this.onSave = () => {
            const { changed } = this.state;
            const { shipper } = this.state;
            const updates = _.pick(shipper, Object.keys(changed));
            if (updates.shorthaul_terms) {
                updates.shorthaul_terms = parseInt(updates.shorthaul_terms, 10);
            }
            if (updates.drayage_terms) {
                updates.drayage_terms = parseInt(updates.drayage_terms, 10);
            }
            // Handle subsidiary field - if it's in the changed fields, send it (including empty string for deselection)
            if ((changed === null || changed === void 0 ? void 0 : changed.subsidiary) && typeof shipper.subsidiary === 'string') {
                updates.subsidiary = shipper.subsidiary.trim();
            }
            updates._id = shipper._id;
            this.setState({ changed: {} }, () => {
                this.props.onSave(updates);
            });
        };
        this.onChangeWatchingStatus = () => {
            this.setState(async (prevState) => {
                const { shipper: { status }, } = prevState;
                const shipper = Object.assign(Object.assign({}, prevState.shipper), { status: status !== 'Service Watch' ? 'Service Watch' : 'Repeat Customer' });
                const payload = { _id: shipper._id, status: shipper.status };
                await this.props.onSave(payload);
                return {
                    shipper,
                };
            });
        };
        this.onCancel = () => {
            const { details: shipper } = this.props; // Remove local changes that didn't save
            const { errors } = this.state;
            _.map(errors, (value, key) => {
                errors[key] = {};
            });
            this.setState({ shipper, changed: {}, errors }, () => {
                this.hideModal();
            });
        };
        this.handleLinesOfBusinessSelect = (linesOfBusiness) => {
            const shipper = Object.assign(Object.assign({}, this.state.shipper), { linesOfBusiness: linesOfBusiness.map((lob) => lob.slug) });
            const changed = Object.assign(Object.assign({}, this.state.changed), { linesOfBusiness: true });
            this.setState((prevState) => {
                return Object.assign(Object.assign({}, prevState), { shipper, changed });
            });
        };
        this.renderFromAddress = (modal) => {
            var _a, _b;
            if (((_a = this.props.details) === null || _a === void 0 ? void 0 : _a.from_address_is_verified) &&
                ((_b = this.props.details) === null || _b === void 0 ? void 0 : _b.from_address)) {
                const className = (modal === MODAL_TYPES.SETTINGS ? 'flex justify-between ' : '').concat('mb-2 -mx-4');
                const { t } = this.props;
                return (React.createElement("div", { className: className },
                    React.createElement("div", { className: "w-1/2 px-4" },
                        React.createElement("span", { className: "form-label" }, titleize(t('FROM_ADDRESS')))),
                    React.createElement("div", { className: "px-4" },
                        React.createElement("span", { className: "text-gray-500" }, this.props.details.from_address))));
            }
            return null;
        };
        this.renderModalComponent = () => {
            const industries = [
                {
                    name: 'Manufacturing and Distribution',
                    localizationSlug: 'MANUFACTURING_DISTRIBUTION',
                },
                { name: 'Carrier', localizationSlug: 'CARRIER' },
                { name: 'Freight forwarder', localizationSlug: 'FREIGHT_FORWARDER' },
                {
                    name: 'Warehouse - 3PL - Broker',
                    localizationSlug: 'WAREHOUSE_3PL_BROKER',
                },
            ];
            const { t } = this.props;
            const { shipper: { first_name, last_name, company_name, markup, email, phone, phone_extension, address, require_tracking_id, business, requires_signature_capture, premium_shipper, cargomatic_transport, enterprise, strategic_shipper, project, test, email_format, primary_reference_field, hide_column_properties, }, errors, showAccountModal, showAddressModal, showSettingsModal, } = this.state;
            const references = ReferenceNumberTypes.map((r) => {
                return {
                    slug: r.name,
                    name: titleize(humanize(r.name)),
                    localizationSlug: r.localizationSlug,
                };
            });
            const linesOfBusiness = LINES_OF_BUSINESS.map((lob) => {
                return Object.assign(Object.assign({}, lob), { selected: this.state.shipper.linesOfBusiness.includes(lob.slug) });
            });
            const hasAdminOrShipperOnboardingRole = SessionStore.hasAdminRole() || SessionStore.hasShipperOnboardingRole();
            return (React.createElement("div", null,
                showAccountModal && (React.createElement("div", null,
                    React.createElement("div", { className: "grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" },
                        React.createElement(CargomaticInput, { className: "sm:col-span-6", error: errors._name, label: `${titleize(t('COMPANY_NAME'))} ${getRequiredAsterisk('company_name')}`, info: t('SHIPPER_COMPANY_NAME'), onChange: (value) => {
                                this.handleFieldChange('company_name', value);
                            }, value: unescapeSpecialChars(company_name), maxLength: 75 }),
                        React.createElement("div", { className: "sm:col-span-3" },
                            React.createElement(CargomaticInput, { error: errors.first_name, label: `${titleize(t('FIRST_NAME'))} ${getRequiredAsterisk('first_name')}`, info: t('SHIPPER_FIRST_NAME'), onChange: (value) => {
                                    this.handleFieldChange('first_name', value);
                                }, value: unescapeSpecialChars(first_name) })),
                        React.createElement("div", { className: "sm:col-span-3" },
                            React.createElement(CargomaticInput, { error: errors.last_name, label: `${titleize(t('LAST_NAME'))} ${getRequiredAsterisk('last_name')}`, info: t('SHIPPER_LAST_NAME'), onChange: (value) => {
                                    this.handleFieldChange('last_name', value);
                                }, value: unescapeSpecialChars(last_name) })),
                        React.createElement("div", { className: "sm:col-span-3" },
                            React.createElement("label", { className: "form-label mb-1" }, `${titleize(t('PHONE'))} ${getRequiredAsterisk('phone')}`),
                            React.createElement(PhoneField, { id: "phone", onChange: (value) => this.handleFieldChange('phone', value), value: phone })),
                        React.createElement("div", { className: "sm:col-span-3" },
                            React.createElement(CargomaticInput, { className: "sm:col-span-3", error: errors.phone_extension, label: ` ${titleize(t('EXT'))} ${getRequiredAsterisk('')}`, info: `${titleize(t('PHONE_NUMBER_EXTENSION_INFO'))}`, onChange: (value) => {
                                    this.handleFieldChange('phone_extension', value);
                                }, value: phone_extension })),
                        React.createElement("div", { className: "sm:col-span-4" },
                            React.createElement(CargomaticInput, { error: errors.email, label: `${titleize(t('EMAIL'))} ${getRequiredAsterisk('email')}`, info: t('SHIPPER_EMAIL'), onChange: (value) => this.handleFieldChange('email', value), value: email, maxLength: 254 })),
                        React.createElement("div", { className: "sm:col-span-2" },
                            React.createElement(CargomaticInput, { error: errors.markup, label: `${titleize(t('MARKUP'))} ${getRequiredAsterisk('markup')}`, info: t('SHIPPER_MARKUP'), onChange: (value) => this.handleFieldChange('markup', value), value: markup })),
                        React.createElement("div", { className: "sm:col-span-3" },
                            React.createElement("label", { className: "mb-1 form-label" }, t('LINES_OF_BUSINESS')),
                            React.createElement(Multiselect, { label: t('LINES_OF_BUSINESS'), items: linesOfBusiness, onSelect: this.handleLinesOfBusinessSelect }))))),
                showAddressModal && hasAdminOrShipperOnboardingRole && (React.createElement("div", null,
                    React.createElement("h4", { className: "mt-0 mb-4 text-grey-darker" }, t('ADDRESS_INFO')),
                    React.createElement("div", { className: "grid sm:grid-cols-3 sm:gap-x-4 gap-y-6" },
                        React.createElement(CargomaticInput, { className: "sm:col-span-3", error: errors.street, label: `${titleize(t('STREET'))} ${getRequiredAsterisk('street')}`, info: t('SHIPPER_STREET_ADDRESS'), onChange: (value) => {
                                this.handleAddressFieldChange('street', value);
                            }, value: address.street }),
                        React.createElement(CargomaticInput, { className: "sm:col-span-3", error: errors.street2, label: `${titleize(t('STREET2'))} ${getRequiredAsterisk('street2')}`, info: t('SHIPPER_STREET2_ADDRESS'), onChange: (value) => {
                                this.handleAddressFieldChange('street2', value);
                            }, value: address.street2 }),
                        React.createElement(CargomaticInput, { error: errors.city, label: `${titleize(t('CITY'))} ${getRequiredAsterisk('city')}`, info: t('THE_SHIPPER_CITY'), onChange: (value) => {
                                this.handleAddressFieldChange('city', value);
                            }, value: address.city }),
                        React.createElement(CargomaticSelect, { error: errors.state, selected: address.state, items: stateCodes, info: t('THE_SHIPPER_STATE'), label: `${titleize(t('STATE'))} ${getRequiredAsterisk('state')}`, textKey: "name", valueKey: "abbreviation", onSelect: (item) => {
                                this.handleAddressFieldChange('state', item.abbreviation);
                            } }),
                        React.createElement(CargomaticInput, { error: errors.zipcode, label: `${titleize(t('ZIP_CODE'))} ${getRequiredAsterisk('zipcode')}`, info: t('SHIPPER_POSTAL_CODE'), onChange: (value) => {
                                this.handleAddressFieldChange('zipcode', value);
                            }, value: address.zipcode })))),
                showSettingsModal && (React.createElement("div", null,
                    React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-x-4" },
                        React.createElement(CargomaticInput, { id: "require_tracking_id", type: "checkbox", error: errors.require_tracking_id, label: `${t('REFERENCE_NUMBER_REQUIRED')} ${getRequiredAsterisk('require_tracking_id')}`, info: t('TRACKING_ID'), onChange: (value) => {
                                this.handleFieldChange('require_tracking_id', value);
                            }, value: require_tracking_id }),
                        React.createElement(CargomaticInput, { id: "business", type: "checkbox", error: errors.business, label: `${titleize(t('BUSINESS'))} ${getRequiredAsterisk('business')}`, info: t('B2B_SHIPPER'), onChange: (value) => this.handleFieldChange('business', value), value: business }),
                        React.createElement(CargomaticInput, { id: "requires_signature_capture", type: "checkbox", error: errors.requires_signature_capture, label: `${t('REQUIRES_SIGNATURE_CAPTURE')} ${getRequiredAsterisk('requires_signature_capture')}`, info: t('SIGNATURE_CAPTURE_TIP'), onChange: (value) => {
                                this.handleFieldChange('requires_signature_capture', value);
                            }, value: requires_signature_capture }),
                        React.createElement(CargomaticInput, { id: "premium_shipper", type: "checkbox", error: errors.premium_shipper, label: `${t('PREMIUM_SHIPPER')} ${getRequiredAsterisk('premium_shipper')}`, info: t('PREMIUM_SHIPPER'), onChange: (value) => {
                                this.handleFieldChange('premium_shipper', value);
                            }, value: premium_shipper }),
                        React.createElement(CargomaticInput, { id: "cargomatic_transport", type: "checkbox", error: errors.cargomatic_transport, label: `${t('CARGOMATIC_TRANSPORT')} ${getRequiredAsterisk('cargomatic_transport')}`, info: t('INVOICE_ENTITY_FOR_ACCOUNT'), onChange: (value) => {
                                this.handleFieldChange('cargomatic_transport', value);
                            }, value: cargomatic_transport }),
                        React.createElement(CargomaticInput, { id: "enterprise", type: "checkbox", error: errors.enterprise, label: `${t('ENTERPRISE_SHIPPER')} ${getRequiredAsterisk('enterprise')}`, info: t('ENTERPRISE_ACCOUNT'), onChange: (value) => {
                                this.handleFieldChange('enterprise', value);
                            }, value: enterprise }),
                        React.createElement(CargomaticInput, { id: "strategic_shipper", type: "checkbox", error: errors.strategic_shipper, label: `${t('STRATEGIC_SHIPPER')} ${getRequiredAsterisk('strategic_shipper')}`, info: t('STRATEGIC_ACCOUNT'), onChange: (value) => {
                                this.handleFieldChange('strategic_shipper', value);
                            }, value: strategic_shipper }),
                        React.createElement(CargomaticInput, { id: "project", type: "checkbox", error: errors.project, label: `${t('PROJECT_SHIPPER')} ${getRequiredAsterisk('project')}`, info: t('PROJECT_ACCOUNT'), onChange: (value) => this.handleFieldChange('project', value), value: project }),
                        React.createElement(CargomaticInput, { id: "test", type: "checkbox", error: errors.test, label: `${t('TEST_USER')} ${getRequiredAsterisk('test')}`, info: t('TEST_ACCOUNT'), onChange: (value) => this.handleFieldChange('test', value), value: test }),
                        React.createElement(CargomaticInput, { id: "hide_driver_property", type: "checkbox", label: `${t('HIDE_DRIVER_PROPERTY')} ${getRequiredAsterisk('HIDE_DRIVER_PROPERTY')}`, info: t('HIDE_DRIVER_DETAILS_SHIPPER_PORTAL'), value: hide_column_properties.includes('driver'), onChange: (value) => this.handleArrayFieldChange('hide_column_properties', value, 'driver') }),
                        React.createElement(CargomaticInput, { id: "hide_carrier_property", type: "checkbox", label: `${t('HIDE_CARRIER_PROPERTY')} ${getRequiredAsterisk('HIDE_CARRIER_PROPERTY')}`, info: t('HIDE_CARRIER_DETAILS_SHIPPER_PORTAL'), value: hide_column_properties.includes('carrier'), onChange: (value) => this.handleArrayFieldChange('hide_column_properties', value, 'carrier') })),
                    React.createElement("hr", null),
                    React.createElement("div", { className: "mt-4 grid sm:grid-cols-2 sm:gap-4" },
                        React.createElement(CargomaticSelect, { selected: email_format, items: [
                                { slug: 'html', name: 'HTML', localizationSlug: 'HTML' },
                                { slug: 'text', name: 'Text', localizationSlug: 'TEXT' },
                            ], label: t('EMAIL_FORMAT_FOR_NOTIFICATION'), info: t('INVOICE_EMAIL_FORMAT'), valueKey: "slug", textKey: "localizationSlug", onSelect: (item) => {
                                this.handleFieldChange('email_format', item.slug);
                            } }),
                        this.renderIndustrySelect(industries),
                        this.accountingAdminAccess() && [
                            React.createElement(CargomaticSelect, { key: "primary_reference_field_select", selected: primary_reference_field, items: references, label: t('PRIMARY_REFERENCE_FIELD'), info: t('PRIMARY_REFERENCE_FIELD_TIP'), valueKey: "slug", textKey: "localizationSlug", onSelect: (item) => {
                                    this.handleFieldChange('primary_reference_field', item.slug);
                                } }),
                        ],
                        this.renderSubsidiarySelect(),
                        this.renderFromAddress(MODAL_TYPES.EDIT)))),
                React.createElement("div", { className: "flex justify-end my-2" },
                    React.createElement("button", { className: "justify-center button button-secondary", onClick: this.onCancel }, t('CANCEL')),
                    React.createElement("button", { className: "mx-2 button button-green", onClick: this.onSave, disabled: this.errorsExist() }, titleize(t('SAVE'))))));
        };
        this.renderAddress = () => {
            const { t } = this.props;
            const { address } = this.props.details;
            const addressString = getAddressString(address);
            return (React.createElement(CargomaticCard, { header: () => (React.createElement("header", { className: "cursor-pointer group", onClick: this.handleShowEditAddressModal },
                    React.createElement("div", { className: "flex items-center justify-between -mx-2" },
                        React.createElement("h3", { className: "px-2 my-0" }, titleize(t('ADDRESS'))),
                        React.createElement(EditButton, null)))) },
                React.createElement("div", { className: "mb-6" },
                    hasAddress(address) && (React.createElement(LabelValue, { value: addressString, icon: "map-marker" })),
                    !hasAddress(address) && (React.createElement(LabelValue, { value: t('NO_ADDRESS_ON_FILE'), icon: "map-marker" })))));
        };
        this.renderSettings = () => {
            const { require_tracking_id, business, requires_signature_capture, premium_shipper, cargomatic_transport, black_and_white_invoices, enterprise, strategic_shipper, project, test, email_format, industry, hide_column_properties, subsidiary, } = this.props.details;
            const { t } = this.props;
            /*
            const { errors, shipper } = this.state;
            */
            return (React.createElement(CargomaticCard, { header: () => (React.createElement("header", { className: "cursor-pointer group", onClick: this.handleShowEditSettingsModal },
                    React.createElement("div", { className: "flex items-center justify-between -mx-2" },
                        React.createElement("h3", { className: "px-2 my-0" }, titleize(t('SETTINGS'))),
                        React.createElement(EditButton, null)))) },
                React.createElement(Capability, { label: t('REQUIRE_TRACKING_ID'), checked: require_tracking_id }),
                React.createElement(Capability, { label: t('B2B_ACCOUNT'), checked: business }),
                React.createElement(Capability, { label: t('REQUIRES_SIGNATURE_CAPTURE'), checked: requires_signature_capture }),
                React.createElement(Capability, { label: t('PREMIUM_SHIPPER'), checked: premium_shipper }),
                React.createElement(Capability, { label: t('CARGOMATIC_TRANSPORT'), checked: cargomatic_transport }),
                React.createElement(Capability, { label: t('ENTERPRISE_SHIPPER'), checked: enterprise }),
                React.createElement(Capability, { label: t('STRATEGIC_SHIPPER'), checked: strategic_shipper }),
                React.createElement(Capability, { label: t('PROJECT_SHIPPER'), checked: project }),
                React.createElement(Capability, { label: t('TEST_ACCOUNT'), checked: test }),
                React.createElement(Capability, { label: t('HIDE_DRIVER_PROPERTY'), checked: hide_column_properties.includes('driver') }),
                React.createElement(Capability, { label: t('HIDE_CARRIER_PROPERTY'), checked: hide_column_properties.includes('carrier') }),
                React.createElement(Capability, { label: t('BLACK_AND_WHITE_INVOICES'), checked: black_and_white_invoices }),
                React.createElement("hr", null),
                React.createElement("div", { className: "flex" },
                    React.createElement("div", { className: "w-full" },
                        React.createElement("div", { className: "flex justify-between mb-2 -mx-4" },
                            React.createElement("div", { className: "w-1/2 px-4" },
                                React.createElement("span", { className: "form-label" }, t('EMAIL_FORMAT'))),
                            React.createElement("div", { className: "px-4" },
                                React.createElement("span", { className: "text-gray-500" }, titleize(t(sluggify(email_format)))))),
                        React.createElement("div", { className: "flex justify-between mb-2 -mx-4" },
                            React.createElement("div", { className: "w-1/2 px-4" },
                                React.createElement("span", { className: "form-label" }, t('INDUSTRY'))),
                            React.createElement("div", { className: "px-4" },
                                React.createElement("span", { className: "text-gray-500" }, industry ? titleize(t(sluggify(industry))) : t('NOT_SET')))),
                        React.createElement("div", { className: "flex justify-between mb-2 -mx-4" },
                            React.createElement("div", { className: "w-1/2 px-4" },
                                React.createElement("span", { className: "form-label" }, t('SUBSIDIARY'))),
                            React.createElement("div", { className: "px-4" },
                                React.createElement("span", { className: "text-gray-500" }, subsidiary ? t(subsidiary.toUpperCase()) : t('NOT_SET')))),
                        this.renderFromAddress(MODAL_TYPES.SETTINGS)))));
        };
        this.handleShowEditAccountModal = () => {
            this.setState({
                showSettingsModal: false,
                showAccountModal: true,
                showAddressModal: false,
            });
        };
        this.handleShowEditAddressModal = () => {
            // Handle possible partial address from brand site signup
            this.validateFields(this.state.shipper.address, [
                'street',
                'street2',
                'city',
                'state',
                'zipcode',
            ]);
            this.setState({
                showSettingsModal: false,
                showAccountModal: false,
                showAddressModal: true,
            });
        };
        this.handleShowEditSettingsModal = () => {
            this.setState({
                showSettingsModal: true,
                showAccountModal: false,
                showAddressModal: false,
            });
        };
        this.onHide = () => {
            if (!this.errorsExist())
                this.hideModal();
        };
        this.state = {
            showAccountModal: false,
            showAddressModal: false,
            showSettingsModal: false,
            errors: {
                first_name: {},
                last_name: {},
                username: {},
                cdl: {},
                phone: {},
                phone_extension: {},
                markup: {},
                email: {},
                address: {},
                authorized: {},
                tsa: {},
                residential: {},
                test: {},
                require_tracking_id: {},
                business: {},
                requires_signature_capture: {},
                premium_shipper: {},
                cargomatic_transport: {},
                primary_reference_field: {},
                enterprise: {},
                strategic_shipper: {},
                project: {},
                beta_tester: {},
                black_and_white_invoices: {},
                invoice_schedule: {},
                drayage_invoice_branding: {},
                drayage_blling_time_frame: {},
                email_format: {},
                accountingHold: {},
                hide_column_properties: {},
                subsidiary: {},
            },
            changed: {},
            shipper: Object.assign({}, props.details),
            eligiblePaymentMethods: [],
            hasLoginAsPermission: false,
            creditCardInfo: null,
            /*
            accountingHold: props.details.accounting
              ? props.details.accounting.on_hold
              : false,
            accountingHoldReason: props.details.accounting
              ? props.details.accounting.reason
              : '',
            accountingHoldError: false,
            */
        };
        if (!this.state.shipper.subsidiary) {
            this.state.shipper.subsidiary = 'Not Set';
        }
        UserActionCreators.getSalespeople();
        UserActionCreators.getAdminUsers({ active: true });
    }
    async componentDidMount() {
        try {
            UserStore.addChangeListener(this._onUserStoreChange);
            const creditCardInfo = await this.getCreditCardInfo();
            const hasLoginAsPermission = await this.hasLoginAsPermission();
            this.setState({
                creditCardInfo,
                hasLoginAsPermission,
            });
        }
        catch (e) {
            console.log('Error: ', e.message);
        }
    }
    componentWillUnmount() {
        UserStore.removeChangeListener(this._onUserStoreChange);
    }
    async UNSAFE_componentWillReceiveProps(nextProps) {
        const { showAccountModal, showAddressModal, showSettingsModal } = this.state;
        if (!showAccountModal && !showAddressModal && !showSettingsModal) {
            const shipper = nextProps.details;
            this.setState({
                shipper,
                hasLoginAsPermission: await this.hasLoginAsPermission(),
            });
        }
    }
    accountingAdminAccess() {
        return SessionStore.hasAccountingAdminRole() || SessionStore.isAdmin();
    }
    hasCreditCardOnFile() {
        var _a;
        return !!((_a = this.state.shipper) === null || _a === void 0 ? void 0 : _a.stripe_customer_id);
    }
    async getCreditCardInfo() {
        var _a;
        if (this.hasCreditCardOnFile() && !this.state.creditCardInfo) {
            const { entity: { data }, } = await getCreditCards((_a = this.state.shipper) === null || _a === void 0 ? void 0 : _a._id);
            return data[0];
        }
        return null;
    }
    async hasLoginAsPermission() {
        const { shipper } = this.state;
        const scopes = await UserWebAPIUtils.getUserContextGrantsByGrantee(shipper._id, ['login']);
        if (scopes === null || scopes === void 0 ? void 0 : scopes.login) {
            return true;
        }
        return false;
    }
    errorsExist() {
        const { errors } = this.state;
        let errorsExist = false;
        _.map(errors, (_value, key) => {
            if (!_.isEmpty(errors[key]))
                errorsExist = true;
        });
        return errorsExist;
    }
    renderIndustrySelect(industries) {
        const { shipper } = this.state;
        const { t } = this.props;
        const selected = shipper.industry ? shipper.industry : null;
        const items = industries.map((industry) => ({
            slug: industry.name,
            name: industry.name,
            localizationSlug: industry.localizationSlug,
        }));
        return (React.createElement(CargomaticSelect, { selected: selected, items: items, label: t('INDUSTRY'), info: t('INDUSTRY_ACCOUNT_TIP'), valueKey: "slug", textKey: "localizationSlug", onSelect: (item) => this.handleFieldChange('industry', item.name) }));
    }
    renderSubsidiarySelect() {
        var _a;
        const { shipper } = this.state;
        const { t } = this.props;
        const selected = (_a = shipper.subsidiary) !== null && _a !== void 0 ? _a : null;
        return (React.createElement(CargomaticSelect, { selected: selected, items: SHIPPER_SUBSIDIARY_OPTIONS, label: t('SUBSIDIARY'), info: t('SUBSIDIARY_PICKLIST_INFO'), valueKey: "value", textKey: "localizationSlug", onSelect: (item) => { var _a; return this.handleFieldChange('subsidiary', (_a = item.value) !== null && _a !== void 0 ? _a : ''); } }));
    }
    hideModal() {
        this.setState({
            showSettingsModal: false,
            showAccountModal: false,
            showAddressModal: false,
        });
    }
    getAccountValue(value) {
        const { t } = this.props;
        return value && (value === null || value === void 0 ? void 0 : value.toLowerCase()) !== 'none'
            ? value
            : decapitalize(t('NONE'));
    }
    render() {
        /*
        const industries = [
          'Manufacturing and Distribution',
          'Carrier',
          'Freight forwarder',
          'Warehouse - 3PL - Broker',
        ];
        */
        // const canEditNetSuiteID = isAdmin || SessionStore.hasFinanceRole();
        // const validationErrors = this.getValidationErrors();
        const { details: { sales_rep, account_manager, operations_manager, status, first_name, last_name, company_name, email, phone, phone_extension, 
        /*
        require_tracking_id,
        business,
        */
        markup,
        /*
        requires_signature_capture,
        premium_shipper,
        cargomatic_transport,
        can_pay_manually,
        enterprise,
        strategic_shipper,
        project,
        beta_tester,
        test,
        black_and_white_invoices,
        invoice_schedule,
        email_format,
        accountingHold,
        */
         }, t, } = this.props;
        const { showAccountModal, showAddressModal, showSettingsModal, hasLoginAsPermission /* , errors */, } = this.state;
        const { userId } = this.props;
        const canWatch = status !== 'New Customer';
        const statusColor = status === 'Service Watch' ? 'brand' : 'blue';
        return (React.createElement("div", null,
            React.createElement(CargomaticCard, null,
                React.createElement("div", { className: "text-center" },
                    React.createElement("div", { className: "flex justify-center mb-2" },
                        React.createElement(Avatar, { key: userId, userId: userId, className: "h-32 w-32 cursor-pointer rounded-full" })),
                    React.createElement("h3", { className: "my-2 text-xl text-grey-darker" }, unescapeSpecialChars(company_name)),
                    React.createElement("div", { className: "flex items-center justify-center -mx-1" },
                        React.createElement("div", { className: "px-1 mb-2" },
                            React.createElement(Badge, { color: "green", rounded: true, size: "large" }, titleize(t('SHIPPER')))),
                        React.createElement("div", { className: "px-1 mb-2" },
                            React.createElement(Badge, { color: statusColor, rounded: true, size: "large" }, status === 'Service Watch'
                                ? t('SERVICE_WATCH')
                                : t('REPEAT_CUSTOMER')))),
                    React.createElement("div", { className: "flex justify-center mt-4 -mx-4" },
                        React.createElement("button", { className: "button text-grey-darker px-0 mr-2", onClick: this.props.onOpenJobs }, t('VIEW_JOBS')),
                        canWatch && (React.createElement("div", { className: "flex items-center px-2" },
                            React.createElement("label", { className: "my-0 mr-2 form-label" }, titleize(t('WATCH'))),
                            React.createElement(Switch, { onColor: "#21b88e", height: 14, width: 28, checked: status === 'Service Watch', onChange: () => this.onChangeWatchingStatus(), className: "" }))),
                        SessionStore.isSupport() && (React.createElement("button", { className: "button text-grey-darker px-0 ml-2 ", onClick: () => loginAs(this.state.shipper._id) }, t('LOG_IN_AS')))))),
            React.createElement("div", { className: "mt-6" },
                React.createElement(CargomaticCard, { header: () => (React.createElement("header", { className: "cursor-pointer group", onClick: this.handleShowEditAccountModal },
                        React.createElement("div", { className: "flex items-center justify-between" },
                            React.createElement("h3", { className: "my-0" }, titleize(t('ACCOUNT'))),
                            React.createElement(EditButton, null)))) },
                    React.createElement(LabelValue, { value: `${unescapeSpecialChars(company_name)}`, icon: "home" }),
                    React.createElement(LabelValue, { value: `${unescapeSpecialChars(first_name)} ${unescapeSpecialChars(last_name)}`, icon: "user" }),
                    React.createElement(LabelValue, { value: createPhoneNumberWithExtension(phone, phone_extension, t('EXT')), icon: "phone" }),
                    React.createElement(LabelValue, { value: `${email}`, icon: "envelope" }),
                    React.createElement(LabelValue, { value: `${markup}`, icon: "usd" }),
                    React.createElement(LabelValue, { value: this.getAccountValue(sales_rep), icon: "trending-up" }),
                    React.createElement(LabelValue, { value: this.getAccountValue(account_manager), icon: "account_manager" }),
                    React.createElement(LabelValue, { value: this.getAccountValue(operations_manager), icon: "operations_manager" }))),
            React.createElement("div", { className: "mt-6" }, this.renderAddress()),
            React.createElement("div", { className: "mt-6" },
                React.createElement(CarrierPreferences, { shipper: this.props.details })),
            React.createElement("div", { className: "mt-6" }, this.renderSettings()),
            React.createElement("div", { className: "my-6" },
                React.createElement("button", { onClick: () => this.props.onOpenPassword(), className: "w-full button button-hollow-red" }, t('CHANGE_PASSWORD'))),
            SessionStore.isSupport() && !hasLoginAsPermission && (React.createElement("div", { className: "my-6" },
                React.createElement("button", { onClick: () => this.props.onOpenAccountRequest(), className: "w-full button button-green" }, t('REQUEST_PERMISSION_TO_LOGIN_AS')))),
            (showAccountModal || showAddressModal || showSettingsModal) && (React.createElement(CargomaticModal, { show: true, onHide: this.onHide, title: t('EDIT_ACCOUNT'), backdrop: "static", body: this.renderModalComponent(), footer: null, location: "middle" }))));
    }
}
ShipperDetails.propTypes = {
    details: PropTypes.object,
    onSave: PropTypes.func.isRequired,
};
export default withTranslation()(ShipperDetails);
